import { useState, useEffect, useCallback } from 'react';
import { useShopPluginApi } from '@app/react-common/api/useShopPluginApi';
import { useAppContext } from '@app/react-common/AppContext';

export interface ShopifyTheme {
  id: number;
  name: string;
  role: 'main' | 'unpublished' | 'demo';
  shopId?: number;
  previewUrl?: string;
  updatedAt: string;
}

const useThemes = (shouldFetch: boolean = true) => {
  const [themes, setThemes] = useState<ShopifyTheme[]>([]);
  const [loading, setLoading] = useState<boolean>(shouldFetch);
  const { showToast } = useAppContext();

  const failureHandler = useCallback(async () => {
    showToast({
      message: `Failed to load themes. Please contact support if the problem persists.`,
      mode: 'danger',
    });
    setLoading(false);
  }, [showToast]);

  const shopPluginApi = useShopPluginApi(failureHandler);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const response = await shopPluginApi.get('/admin/themes');
      setThemes(response.data as ShopifyTheme[]);
    } catch (error) {
      console.error('Error fetching themes:', error);
      setThemes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch themes if shouldFetch is true
    if (shouldFetch) {
      fetchThemes();
    } else {
      setLoading(false);
    }
  }, [shouldFetch]);

  return {
    themes,
    loading,
  };
};

export default useThemes;
